import axios from 'axios';
import { apiClient } from './api';
import type { WFHRequest, WFHRequestWithUser, CreateWFHRequest, ReviewRequest } from '../types/wfh.types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const msg = err.response?.data?.message as string | undefined;
    return msg ?? fallback;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export async function submitRequest(data: CreateWFHRequest): Promise<WFHRequest> {
  try {
    const response = await apiClient.post<ApiResponse<WFHRequest>>('/api/wfh/request', data);
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to submit request. Please try again.'));
  }
}

export async function cancelRequest(requestId: string): Promise<void> {
  try {
    await apiClient.delete(`/api/wfh/request/${requestId}`);
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to cancel request. Please try again.'));
  }
}

export async function getMyRequests(): Promise<{ upcoming: WFHRequest[]; past: WFHRequest[] }> {
  try {
    const response = await apiClient.get<ApiResponse<{ upcoming: WFHRequest[]; past: WFHRequest[] }>>('/api/wfh/my-requests');
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to load your requests.'));
  }
}

export async function getPendingRequests(): Promise<WFHRequestWithUser[]> {
  try {
    const response = await apiClient.get<ApiResponse<WFHRequestWithUser[]>>('/api/wfh/pending');
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to load pending requests.'));
  }
}

export async function reviewRequest(
  requestId: string,
  review: ReviewRequest
): Promise<WFHRequest> {
  try {
    const response = await apiClient.patch<ApiResponse<WFHRequest>>(
      `/api/wfh/request/${requestId}/review`,
      review
    );
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, 'Failed to submit review. Please try again.'));
  }
}

export async function getTodayApprovedRequest(): Promise<WFHRequest | null> {
  try {
    const response = await apiClient.get<ApiResponse<WFHRequest | null>>('/api/wfh/today');
    return response.data.data;
  } catch (err: unknown) {
    throw new Error(extractErrorMessage(err, "Failed to check today's request status."));
  }
}
